import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Image } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { formatDateToDateDisplay } from '@/utils/formatDateToDateDisplay';
import { formatDateToTimeDisplay } from '@/utils/formatDateToTimeDisplay';
import { CalendarEvent } from '@/model';
import { useTranslation } from 'react-i18next';

export interface CalendarEventTableProps {
  data: CalendarEvent[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: CalendarEvent) => void;
  onDeleteClick: (item: CalendarEvent) => void;
}

export const CalendarEventTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: CalendarEventTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<{
    team_name: string;
    team_image: string;
    event_at: string;
    place: string;
    name: string;
  }>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    columnHelper.accessor('event_at', {
      id: 'event_date',
      cell: props => formatDateToDateDisplay(props.getValue()),
      header: t('pages.application_calendar_events.components.calendar_events_table.date'),
    }),
    columnHelper.accessor('event_at', {
      id: 'event_time',
      cell: props => formatDateToTimeDisplay(props.getValue()),
      header: t('pages.application_calendar_events.components.calendar_events_table.time'),
    }),

    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_calendar_events.components.calendar_events_table.team'),
    }),

    columnHelper.accessor('place', {
      cell: props => props.getValue(),
      header: t('pages.application_calendar_events.components.calendar_events_table.place'),
    }),
    columnHelper.accessor('name', {
      cell: props => props.getValue(),
      header: t('pages.application_calendar_events.components.calendar_events_table.event_name'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.application_calendar_events.components.calendar_events_table.actions'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_calendar_events.components.calendar_events_table.menu_button_label')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as CalendarEvent)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_calendar_events.components.calendar_events_table.view_details')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as CalendarEvent)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_calendar_events.components.calendar_events_table.delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
